import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';

import { messages } from '../i18n';
import { type Integration } from '../../types';
import { useAnalytics } from '../../analytics';
import ConnectIntegrationMenuItem from './ConnectIntegrationMenuItem';
import ManageIntegrationMenuItem from './ManageIntegrationMenuItem';
import { useThirdPartyState } from '../ThirdParty';
import { DropdownMenuAnalytics } from './DropdownMenuAnalytics';

const ThirdPartyMenuGroup = () => {
	const analytics = useAnalytics(DropdownMenuAnalytics);
	const { formatMessage } = useIntl();
	const {
		enabledIntegrations,
		integrations: connectedIntegrations,
		onConnectProvider,
		onDisconnectProvider,
	} = useThirdPartyState();

	const connectableIntegrations = useMemo(
		() => [...enabledIntegrations].filter((itg) => !connectedIntegrations.has(itg)),
		[connectedIntegrations, enabledIntegrations],
	);

	const handleConnectItemClick = useCallback(
		(serviceKey: Integration) => () => {
			analytics.dropdownMenuItemClicked({
				actionSubjectId: 'connectIntegration',
				attributes: {
					integration: serviceKey,
				},
			});

			if (onConnectProvider) {
				onConnectProvider({
					serviceKey,
				});
			}
		},
		[analytics, onConnectProvider],
	);

	const handleManageConnectionItemClick = useCallback(
		(serviceKey: Integration) => () => {
			analytics.dropdownMenuItemClicked({
				actionSubjectId: 'manageIntegration',
				attributes: {
					integration: serviceKey,
				},
			});

			if (onDisconnectProvider) {
				onDisconnectProvider(serviceKey);
			}
		},
		[analytics, onDisconnectProvider],
	);

	return (
		<>
			{connectableIntegrations.length > 0 && (
				<DropdownItemGroup title={formatMessage(messages.connectToDropdownItemGroupTitle)}>
					{connectableIntegrations.map((integration: Integration) => (
						<ConnectIntegrationMenuItem
							key={`${integration}`}
							onClick={handleConnectItemClick(integration)}
							integration={integration}
						/>
					))}
				</DropdownItemGroup>
			)}
			{connectedIntegrations.size > 0 && (
				<DropdownItemGroup title={formatMessage(messages.manageConnectionsDropItemGroupTitle)}>
					{[...connectedIntegrations].map((integration) => (
						<ManageIntegrationMenuItem
							key={`${integration}`}
							onClick={handleManageConnectionItemClick(integration)}
							integration={integration}
						/>
					))}
				</DropdownItemGroup>
			)}
		</>
	);
};

export default ThirdPartyMenuGroup;
